import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, ApiError } from '../lib/api';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [values, setValues] = useState({ full_name: '', email: '', password: '', desired_site_name: '', motivation: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [baseDomain, setBaseDomain] = useState('occiput.io');

  useEffect(() => {
    api.get<{ base_domain?: string }>('/settings')
      .then(s => { if (s.base_domain) setBaseDomain(s.base_domain); })
      .catch(() => { /* use default */ });
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setValues(v => ({ ...v, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!values.full_name || !values.email || !values.password || !values.desired_site_name) {
      setError('Vul alle verplichte velden in.');
      return;
    }
    if (values.password.length < 8) { setError('Wachtwoord moet minimaal 8 tekens zijn.'); return; }

    setIsSubmitting(true);
    setError('');
    try {
      await register(values);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Er is een fout opgetreden.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="h-16 w-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Aanvraag ontvangen!</h1>
          <p className="text-gray-600 mb-4">
            Bedankt voor je aanmelding. We bekijken iedere aanvraag handmatig om de kwaliteit van het platform te bewaken.
          </p>
          <p className="text-gray-600 mb-8">
            Je ontvangt bericht zodra je aanvraag is beoordeeld (1-3 werkdagen).
          </p>
          <div className="card p-6 text-left mb-8">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Wat gebeurt er nu?</h2>
            <ol className="space-y-3">
              {['We controleren je aanvraag.', 'Je ontvangt een e-mail met instructies.', 'Je kunt inloggen en je site opzetten.'].map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-brand-100 text-brand-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
          <Link to="/" className="btn-secondary">Terug naar de homepage</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="block text-center text-2xl font-bold text-brand-600 mb-8">Occiput</Link>
        <h1 className="text-center text-2xl font-bold text-gray-900">Aanmelden voor toegang</h1>
        <p className="mt-2 text-center text-sm text-gray-600">We bekijken iedere aanvraag handmatig.</p>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card p-8">
          {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="label">Volledige naam *</label>
              <input name="full_name" type="text" value={values.full_name} onChange={handleChange} className="input" />
            </div>
            <div>
              <label className="label">E-mailadres *</label>
              <input name="email" type="email" value={values.email} onChange={handleChange} className="input" />
            </div>
            <div>
              <label className="label">Wachtwoord * (min. 8 tekens)</label>
              <input name="password" type="password" value={values.password} onChange={handleChange} className="input" />
            </div>
            <div>
              <label className="label">Gewenste sitenaam *</label>
              <div className="flex">
                <input name="desired_site_name" type="text" value={values.desired_site_name} onChange={handleChange} className="input rounded-r-none" placeholder="mijn-site" />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-sm text-gray-500">.{baseDomain}</span>
              </div>
            </div>
            <div>
              <label className="label">Motivatie (optioneel)</label>
              <textarea name="motivation" rows={3} value={values.motivation} onChange={handleChange} className="input resize-none" />
            </div>
            <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-2.5">
              {isSubmitting ? 'Aanmelden...' : 'Aanmelden'}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-gray-600">Al een account? <Link to="/login" className="text-brand-600 font-medium hover:underline">Inloggen</Link></p>
        </div>
      </div>
    </div>
  );
}
